import { useState, type FunctionComponent } from "react";
import { PlusOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Popconfirm, Segmented, Space, Table, Tag, Typography, message } from "antd";
import dayjs, { type Dayjs } from "dayjs";

import {
  ChallengeEventState,
  getChallengeEventState,
  isSafeChallengeEventLink,
  type ChallengeEventEntity,
} from "@fitness-app/data-models/entities/Challenge";

import { supabase } from "~/store/initializeStore";
import ChallengeEventFormModal from "./ChallengeEventFormModal";
import ChallengeEventsCalendar from "./ChallengeEventsCalendar";

type Props = {
  challengeId: string;
  challengeStartDate: string | null | undefined;
  challengeEndDate: string | null | undefined;
  /** Drives whether an event may host a discussion thread (TRE-1654). */
  challengeFeedMode: string | null | undefined;
};

const eventStateTag: Record<ChallengeEventState, { color: string; label: string }> = {
  [ChallengeEventState.Live]: { color: "red", label: "Trwa teraz" },
  [ChallengeEventState.Upcoming]: { color: "green", label: "Nadchodzące" },
  [ChallengeEventState.Past]: { color: "default", label: "Minione" },
};

const formatEventTime = (event: ChallengeEventEntity): string => {
  const start = dayjs(event.startAt);
  const startLabel = start.format("DD.MM.YYYY (ddd) HH:mm");
  if (!event.endAt) {
    return startLabel;
  }
  const end = dayjs(event.endAt);
  return `${startLabel} – ${end.isSame(start, "day") ? end.format("HH:mm") : end.format("DD.MM.YYYY HH:mm")}`;
};

const ChallengeEventsTable: FunctionComponent<Props> = ({
  challengeId,
  challengeStartDate,
  challengeEndDate,
  challengeFeedMode,
}) => {
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<ChallengeEventEntity | null>(null);
  const [view, setView] = useState<"list" | "calendar">("calendar");
  /** Slot picked on the calendar — the form opens there instead of at the challenge default. */
  const [draftSlot, setDraftSlot] = useState<{
    startAt: Dayjs;
    durationMinutes: number | null;
    repeat?: "once" | "weekly";
  } | null>(null);

  const eventsQuery = useQuery({
    queryKey: ["challenge-events", challengeId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("challenge_event")
        .select("*")
        .eq("challengeId", challengeId)
        .order("startAt", { ascending: true })
        .returns<ChallengeEventEntity[]>();

      if (error) {
        throw error;
      }

      return data ?? [];
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async ({ event, wholeSeries }: { event: ChallengeEventEntity; wholeSeries: boolean }) => {
      const query = supabase.from("challenge_event").delete();
      const { error } =
        wholeSeries && event.seriesId
          ? await query.eq("challengeId", challengeId).eq("seriesId", event.seriesId)
          : await query.eq("id", event.id);

      if (error) {
        throw new Error(error.message);
      }
    },
    onSuccess: (_data, { wholeSeries }) => {
      void message.success(wholeSeries ? "Seria usunięta" : "Wydarzenie usunięte");
      void queryClient.invalidateQueries({ queryKey: ["challenge-events", challengeId] });
    },
    onError: (error: Error) => {
      void message.error(`Nie udało się usunąć: ${error.message}`);
    },
  });

  /** Drag-and-drop on the calendar moves ONE occurrence — the rest of the series stays put. */
  const moveMutation = useMutation({
    mutationFn: async ({ event, startAt }: { event: ChallengeEventEntity; startAt: Dayjs }) => {
      const durationMinutes = event.endAt ? dayjs(event.endAt).diff(dayjs(event.startAt), "minute") : null;
      const { error } = await supabase
        .from("challenge_event")
        .update({
          startAt: startAt.toISOString(),
          endAt: durationMinutes ? startAt.add(durationMinutes, "minute").toISOString() : null,
          updatedAt: new Date().toISOString(),
        } as never)
        .eq("id", event.id);

      if (error) {
        throw new Error(error.message);
      }
    },
    onSuccess: () => {
      void message.success("Termin przeniesiony");
    },
    onError: (error: Error) => {
      void message.error(`Nie udało się przenieść terminu: ${error.message}`);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ["challenge-events", challengeId] });
    },
  });

  const events = eventsQuery.data ?? [];

  const openCreate = () => {
    setEditingEvent(null);
    setDraftSlot(null);
    setIsFormOpen(true);
  };

  const openCreateAt = (startAt: Dayjs, durationMinutes: number | null, repeat?: "once" | "weekly") => {
    setEditingEvent(null);
    setDraftSlot({ startAt, durationMinutes, repeat });
    setIsFormOpen(true);
  };

  const openEdit = (event: ChallengeEventEntity) => {
    setEditingEvent(event);
    setDraftSlot(null);
    setIsFormOpen(true);
  };

  return (
    <Space direction="vertical" size={12} style={{ width: "100%" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
        <Segmented
          value={view}
          onChange={(value) => setView(value as "list" | "calendar")}
          options={[
            { value: "calendar", label: "Kalendarz" },
            { value: "list", label: "Lista" },
          ]}
        />
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Dodaj wydarzenie
        </Button>
      </div>

      {view === "calendar" ? (
        <ChallengeEventsCalendar
          events={events}
          challengeStartDate={challengeStartDate}
          challengeEndDate={challengeEndDate}
          loading={eventsQuery.isFetching || moveMutation.isLoading}
          onCreateAt={openCreateAt}
          onEdit={openEdit}
          onMove={(event, startAt) => moveMutation.mutate({ event, startAt })}
        />
      ) : (
        <Table<ChallengeEventEntity>
          dataSource={events}
          rowKey="id"
          size="middle"
          scroll={{ x: true }}
          loading={eventsQuery.isFetching}
          locale={{ emptyText: "Brak wydarzeń w tym wyzwaniu" }}
          pagination={false}
        >
          <Table.Column<ChallengeEventEntity>
            title="Wydarzenie"
            dataIndex="title"
            key="title"
            render={(title, row) => (
              <Space direction="vertical" size={0}>
                <Typography.Text strong>{title}</Typography.Text>
                {row.description ? (
                  <Typography.Text type="secondary" style={{ maxWidth: 360 }}>
                    {row.description}
                  </Typography.Text>
                ) : null}
              </Space>
            )}
          />
          <Table.Column<ChallengeEventEntity>
            title="Termin"
            key="startAt"
            render={(_, row) => {
              const state = getChallengeEventState(row);
              return (
                <Space direction="vertical" size={2}>
                  <Typography.Text>{formatEventTime(row)}</Typography.Text>
                  <Tag color={eventStateTag[state].color}>{eventStateTag[state].label}</Tag>
                </Space>
              );
            }}
          />
          <Table.Column<ChallengeEventEntity>
            title="Link"
            key="linkUrl"
            // Rows written outside the form (import, SQL, the recurring copy task) are not covered
            // by its regex, so the scheme is re-checked before the URL reaches an href.
            render={(_, row) =>
              isSafeChallengeEventLink(row.linkUrl) ? (
                <Typography.Link href={row.linkUrl} target="_blank" rel="noreferrer">
                  {row.linkLabel || row.linkUrl}
                </Typography.Link>
              ) : (
                <Typography.Text type="secondary">-</Typography.Text>
              )
            }
          />
          <Table.Column<ChallengeEventEntity>
            title="Seria"
            key="seriesId"
            render={(_, row) =>
              row.seriesId ? <Tag color="gold">Cykliczne</Tag> : <Typography.Text type="secondary">-</Typography.Text>
            }
          />
          <Table.Column<ChallengeEventEntity>
            title="Opcje"
            key="action"
            render={(_, row) => (
              <Space size="middle">
                <Button type="link" className="px-0" onClick={() => openEdit(row)}>
                  Edytuj
                </Button>
                <Popconfirm
                  title="Usunąć ten termin?"
                  okText="Usuń"
                  cancelText="Anuluj"
                  okButtonProps={{ danger: true, loading: deleteMutation.isLoading }}
                  onConfirm={() => deleteMutation.mutate({ event: row, wholeSeries: false })}
                >
                  <Button type="link" danger className="px-0">
                    Usuń
                  </Button>
                </Popconfirm>
                {row.seriesId ? (
                  <Popconfirm
                    title="Usunąć wszystkie terminy tej serii?"
                    okText="Usuń serię"
                    cancelText="Anuluj"
                    okButtonProps={{ danger: true, loading: deleteMutation.isLoading }}
                    onConfirm={() => deleteMutation.mutate({ event: row, wholeSeries: true })}
                  >
                    <Button type="link" danger className="px-0">
                      Usuń serię
                    </Button>
                  </Popconfirm>
                ) : null}
              </Space>
            )}
          />
        </Table>
      )}

      <ChallengeEventFormModal
        open={isFormOpen}
        challengeId={challengeId}
        challengeStartDate={challengeStartDate}
        challengeEndDate={challengeEndDate}
        challengeFeedMode={challengeFeedMode}
        event={editingEvent}
        defaultStart={draftSlot?.startAt ?? null}
        defaultDurationMinutes={draftSlot?.durationMinutes ?? null}
        defaultRepeat={draftSlot?.repeat}
        onClose={() => {
          setIsFormOpen(false);
          setEditingEvent(null);
          setDraftSlot(null);
        }}
      />
    </Space>
  );
};

export default ChallengeEventsTable;
